import { LoadingState } from 'src/app/utilities/state.util';
import {
    BlueSageUnmatchedPaymentRowFragment,
    GetBlueSageJobLookupQuery,
    GetBlueSageUnmatchedSummaryQuery,
} from 'src/generated/graphql.generated';

/** What a job code resolved to — the matched job, or why nothing matched (K25). */
export type BlueSageJobMatch = GetBlueSageJobLookupQuery['blueSageJobLookup'];

/**
 * The BlueSage review queue — K8.
 *
 * Payments that reached a terminal without the CRM asking for them and could
 * not be attributed to one job. Until this existed the CRM recognised such an
 * event and then only logged it: real money had moved and there was no record
 * of it anywhere in the app.
 *
 * Requirement: K8 - `summary` is retained separately from `page` so the count
 * can be surfaced anywhere (a nav badge, a dashboard tile) without loading a
 * page of rows.
 *
 * `summary` is held separately from `page` on purpose. The count is meant to be
 * referenced across the app — a nav badge, a dashboard tile — and it comes from
 * its own cheap query, so nothing has to load a page of rows to show a number.
 */
export const blueSageQueueInitialState = {
    page: undefined as {
        total: number;
        skipped: number;
        limited: number;
        payments: BlueSageUnmatchedPaymentRowFragment[];
    } | undefined,
    summary: [] as GetBlueSageUnmatchedSummaryQuery['blueSageUnmatchedPaymentsSummary'],

    loading: LoadingState.INIT as LoadingState,
    /**
     * True when the last load FAILED, as distinct from succeeding with nothing.
     *
     * `LoadingState` has no ERROR member, so a failed load settled on LOADED and
     * the table rendered its empty message: **"Nothing here — every terminal
     * payment has been attributed."** On a screen whose entire job is to show
     * money nobody has claimed, a backend hiccup therefore read as "all clear".
     * Seen for real on 2026-08-18, during a backend restart.
     */
    loadFailed: false as boolean,
    /** Id of the payment currently being attached or dismissed, if any. */
    resolvingId: undefined as string | undefined,
    /** The hold a *Complete this hold* press is acting on, so only that button spins. */
    resolvingHoldId: undefined as string | undefined,

    /** `undefined` shows every status; the page defaults to what still needs a human. */
    statusFilter: 'open' as string | undefined,
    skip: 0 as number,
    limit: 25 as number,

    /** What the operator has typed into the resolve form for the selected row. */
    selectedId: undefined as string | undefined,

    /**
     * The JOB CODE, not a job id.
     *
     * This field asked for a `Job.id` until 2026-08-18, which is not something
     * anybody has: the terminal receipt carries a code, the queue row carries a
     * code, and typing the code that is printed in front of you produced a raw
     * AJV 400 because the platform's id pattern is lower-case only. The code is
     * resolved to a job through `blueSageJobLookup`, and the attach mutation
     * still takes the resolved UUID.
     */
    jobCode: '' as string,
    /** What that code resolved to. `undefined` means "not looked up yet". */
    jobMatch: undefined as BlueSageJobMatch | undefined,
    lookingUp: false as boolean,

    /**
     * The lookup itself failed — as opposed to running and finding nothing.
     *
     * Without this the two were indistinguishable on screen: a failed query
     * cleared `jobMatch` and stopped, which renders exactly like a code nobody
     * has typed yet. An operator who searches a code they can SEE on a job and
     * gets a blank panel concludes the search does not work, which is what YMM
     * reported on 2026-08-28 for a code that does exist.
     *
     * A lookup that runs and finds nothing already speaks for itself through
     * `jobMatch.detail`; this is only for the case where it never ran.
     */
    lookupFailed: false as boolean,

    dismissReason: '' as string,
};

export type BlueSageQueueState = typeof blueSageQueueInitialState;
